import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MeetingEventDto, TimetableControllerService, TimetableDto,} from '@api/utisha-backend';
import {ResponsiveService} from '../services/responsive.service';
import {ZonedDatePipe} from '../pipes/zoned-date-pipe';
import {instantToLocalDateString} from '../util/local-date';

class EventDateBucket {
  date: string;
  events: MeetingEventDto[];
}

class GridEventItem {
  data?: MeetingEventDto;
}

@Component({
  selector: 'app-timetable',
  templateUrl: './timetable.component.html',
  styleUrls: ['./timetable.component.scss'],
  imports: [ZonedDatePipe],
})
export class TimetableComponent implements OnInit {
  dates: string[];
  dateBuckets: EventDateBucket[];
  events: MeetingEventDto[];
  gridItems: GridEventItem[] = [];
  maxEventsPerDay: number;
  mode: string;
  size = 'l';
  withDates = true;
  theme: 'grey';
  startDayOfWeek: string;
  timezone = 'CET';

  constructor(
    private timetableController: TimetableControllerService,
    public responsive: ResponsiveService,
    private datePipe: ZonedDatePipe,
    private route: ActivatedRoute,
  ) {
    this.route.queryParams.subscribe((params) => {
      this.mode = params.mode;
      this.size = params.size;
      this.withDates = !params.noDates;
      this.theme = params.theme || 'grey';
      this.startDayOfWeek = params.startDayOfWeek;
      this.timezone = params.timezone || 'CET';
    });
  }

  get rowHeight(): string {
    if (this.mode === 'horizontal' || this.responsive.isMobile) {
      switch (this.size) {
        case 'xxs':
          return '24px';
        case 'xs':
          return '28px';
        case 'md':
          return '36px';
        case 'l':
          return '42px';
        case 'xl':
          return '50px';
        default:
          return '36px';
      }
    } else {
      switch (this.size) {
        case 'xxs':
          return '50px';
        case 'xs':
          return '50px';
        case 'md':
          return '75px';
        case 'l':
          return '100px';
        case 'xl':
          return '120px';
        default:
          return '125px';
      }
    }
  }

  get eventRowHeight(): string {
    if (this.mode === 'horizontal' || this.responsive.isMobile) {
      switch (this.size) {
        case 'xxs':
          return '50px';
        case 'xs':
          return '50px';
        case 'md':
          return '75px';
        case 'l':
          return '100px';
        case 'xl':
          return '120px';
        default:
          return '125px';
      }
    } else {
      switch (this.size) {
        case 'xxs':
          return '50px';
        case 'xs':
          return '50px';
        case 'md':
          return '75px';
        case 'l':
          return '100px';
        case 'xl':
          return '120px';
        default:
          return '125px';
      }
    }
  }

  ngOnInit(): void {
    this.timetableController
      .getTimetableEvents(this.timezone)
      .subscribe((timetable) => this.createDateBuckets(timetable));
  }

  createDateBuckets(timetable: TimetableDto): void {
    let dateBuckets: EventDateBucket[] =
      timetable.dates?.map(
        (d) =>
          ({
            date: d.substring(0, 10),
            events: [],
          }) as EventDateBucket,
      ) || [];
    for (const event of timetable.events || []) {
      const d = instantToLocalDateString(event.startDateTimeInstant);
      if (!d) continue;
      let bucket = dateBuckets.find((b) => b.date === d);
      if (!bucket) {
        bucket = new EventDateBucket();
        bucket.date = d;
        bucket.events = [];
        dateBuckets.push(bucket);
      }
      bucket.events.push(event);
    }
    if (this.startDayOfWeek) {
      const idx = dateBuckets.findIndex(
        (b) =>
          this.datePipe.transform(b.date, 'dayOfWeek')?.toLowerCase() ===
          this.startDayOfWeek.toLowerCase(),
      );
      if (idx > 0) {
        dateBuckets = dateBuckets
          .splice(idx)
          .concat(dateBuckets.splice(0, idx));
      }
    }
    this.dates = dateBuckets.map((b) => b.date);
    this.maxEventsPerDay = Math.max(
      ...dateBuckets.map((b) => (b.events ? b.events.length : 0)),
    );
    this.dateBuckets = dateBuckets;
    this.gridItems = [];
    for (let i = 0; i < this.maxEventsPerDay; i++) {
      for (const bucket of dateBuckets) {
        if (bucket.events && bucket.events.length > i) {
          this.gridItems.push({ data: bucket.events[i] });
        } else {
          this.gridItems.push({ data: undefined });
        }
      }
    }
  }

  isEven(i: number): boolean {
    return i % 2 === 0;
  }

  isLastRow(i: number): boolean {
    return this.dates && i >= this.dates.length * (this.maxEventsPerDay - 1);
  }
}
